import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { authApi } from './api/auth';
import type { Me } from './api/auth';
import { AboutScreen } from './screens/AboutScreen';
import { AdminInvitesScreen } from './screens/AdminInvitesScreen';
import { CalendarSubscriptionScreen } from './screens/CalendarSubscriptionScreen';
import { DrivingScreen } from './screens/DrivingScreen';
import { EditEventScreen } from './screens/EditEventScreen';
import { EventDetailScreen } from './screens/EventDetailScreen';
import { EventTypesScreen } from './screens/EventTypesScreen';
import { PackingListsScreen } from './screens/PackingListsScreen';
import { ManageScreen } from './screens/ManageScreen';
import { ReportsScreen } from './screens/ReportsScreen';
import { PhotosScreen } from './screens/PhotosScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { NotificationSettingsScreen } from './screens/NotificationSettingsScreen';
import { FamilyScreen } from './screens/FamilyScreen';
import { FamilyConversationsScreen } from './screens/FamilyConversationsScreen';
import { FamilyEventScreen } from './screens/FamilyEventScreen';
import { FamilyThreadScreen } from './screens/FamilyThreadScreen';
import { FoundUnitScreen } from './screens/FoundUnitScreen';
import { HomeScreen } from './screens/HomeScreen';
import { NewAnnouncementScreen } from './screens/NewAnnouncementScreen';
import { LiveThreadScreen } from './screens/LiveThreadScreen';
import { ChannelSettingsScreen } from './screens/ChannelSettingsScreen';
import { PhotoSettingsScreen } from './screens/PhotoSettingsScreen';
import { ConversationsScreen } from './screens/ConversationsScreen';
import { NewConversationScreen } from './screens/NewConversationScreen';
import { NewEventScreen } from './screens/NewEventScreen';
import { RosterScreen } from './screens/RosterScreen';
import { EventRosterScreen } from './screens/EventRosterScreen';
import { GearSheetsScreen } from './screens/GearSheetsScreen';
import { SignInScreen } from './screens/SignInScreen';
import { SubgroupsScreen } from './screens/SubgroupsScreen';
import { UnitHomeScreen } from './screens/UnitHomeScreen';
import { PrivacyScreen } from './screens/PrivacyScreen';
import { TermsScreen } from './screens/TermsScreen';
import { LiveMessagesProvider } from './hooks/useLiveMessages';
import { FOOTER_LINKS } from './lib/legalDocuments';
import type { FooterPath } from './lib/legalDocuments';
import './styles/app.css';

/**
 * What sits behind each footer destination.
 *
 * <b>A `Record` keyed on the union, so the compiler is what checks the link.</b> Adding a path to
 * `FOOTER_LINKS` without adding it here does not build — which is the guarantee TROOP-203 is buying,
 * since the two dead anchors it replaces survived a year of builds, tests and browsers precisely
 * because nothing anywhere connected the link to the thing it pointed at.
 */
const FOOTER_SCREENS: Record<FooterPath, ReactElement> = {
  '/about': <AboutScreen />,
  '/privacy': <PrivacyScreen />,
  '/terms': <TermsScreen />,
};

function useSession() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    setMe(await authApi.me());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { me, refresh };
}

/** Founding flow: sign in with the invite, then name the unit. */
function StartRoute({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [inviteId, setInviteId] = useState<string | null>(null);

  const token = params.get('invite');

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (inviteId) {
    return (
      <FoundUnitScreen
        inviteId={inviteId}
        onCreated={async (unitId) => {
          await onSignedIn();
          navigate(`/units/${unitId}`, { replace: true });
        }}
      />
    );
  }

  return (
    <SignInScreen
      mode="invite"
      inviteToken={token}
      onSignedIn={({ inviteId: id }) => {
        if (id) setInviteId(id);
      }}
    />
  );
}

function ClaimRoute({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return (
    <SignInScreen
      mode="claim"
      claimToken={token}
      onSignedIn={async () => {
        await onSignedIn();
        navigate('/', { replace: true });
      }}
    />
  );
}

function UnitHomeRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <UnitHomeScreen
      unitId={unitId}
      unitName={membership.unitName}
      // The API is the enforcement point; this only decides what renders, so a member is not
      // shown a button that would be refused.
      canCreateEvents={membership.permissionLevel !== 'Member'}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function NewEventRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership || membership.permissionLevel === 'Member') {
    return <Navigate to={unitId ? `/units/${unitId}` : '/'} replace />;
  }

  return (
    <NewEventScreen
      unitId={unitId}
      unitName={membership.unitName}
      unitTimeZoneId={membership.timeZoneId}
      allowsYouthParticipants={membership.allowsYouthParticipants}
      onCreated={(eventId) => navigate(`/units/${unitId}/events/${eventId}`, { replace: true })}
      onCancel={() => navigate(`/units/${unitId}`)}
    />
  );
}

function NewAnnouncementRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership || membership.permissionLevel === 'Member') {
    return <Navigate to={unitId ? `/units/${unitId}` : '/'} replace />;
  }

  return (
    <NewAnnouncementScreen
      unitId={unitId}
      unitName={membership.unitName}
      allowsYouthParticipants={membership.allowsYouthParticipants}
      // Back to the unit page, which is where the announcement now sits at the top. There is no
      // announcement detail screen: the card IS the record, in full, wherever it renders.
      onPublished={() => navigate(`/units/${unitId}`, { replace: true })}
      onCancel={() => navigate(`/units/${unitId}`)}
    />
  );
}

function EventRoute({ me }: { me: Me }) {
  const { unitId, eventId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !eventId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <EventDetailScreen
      unitId={unitId}
      eventId={eventId}
      me={me}
      meId={me.personId}
      onNavigate={(path) => navigate(path)}
      // The API is the enforcement point; this only decides what renders, so a member is not
      // shown a way in that would be refused.
      canEdit={membership.permissionLevel !== 'Member'}
      subgroupLabelSingular={membership.subgroupLabelSingular}
      subgroupLabelPlural={membership.subgroupLabelPlural}
      onBack={() => navigate(`/units/${unitId}`)}
      onEdit={() => navigate(`/units/${unitId}/events/${eventId}/edit`)}
      onOpenThread={(conversationId) =>
        navigate(`/units/${unitId}/conversations/${conversationId}`)
      }
    />
  );
}

function EditEventRoute({ me }: { me: Me }) {
  const { unitId, eventId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !eventId || !membership || membership.permissionLevel === 'Member') {
    return <Navigate to={unitId && eventId ? `/units/${unitId}/events/${eventId}` : '/'} replace />;
  }

  const back = () => navigate(`/units/${unitId}/events/${eventId}`, { replace: true });

  return <EditEventScreen unitId={unitId} eventId={eventId} onSaved={back} onCancel={back} />;
}

function EventTypesRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <EventTypesScreen
      unitId={unitId}
      unitName={membership.unitName}
      // A member reaches this screen and reads it. The controls are absent rather than disabled,
      // and the screen names who does act -- which is more use than a grey button.
      canManage={membership.permissionLevel !== 'Member'}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function PackingListsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <PackingListsScreen
      unitId={unitId}
      unitName={membership.unitName}
      // A member reaches this screen and keeps their own lists here. What they cannot do is write
      // the unit's -- and the form says so by offering "mine" rather than by greying anything out.
      canManage={membership.permissionLevel !== 'Member'}
      subgroupLabelSingular={membership.subgroupLabelSingular}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function SubgroupsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <SubgroupsScreen
      unitId={unitId}
      unitName={membership.unitName}
      // Straight from the unit. Nothing here decides whether the word is "Patrol" or "Den".
      labelSingular={membership.subgroupLabelSingular}
      labelPlural={membership.subgroupLabelPlural}
      canManage={membership.permissionLevel !== 'Member'}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/**
 * The unit's photo feed — TROOP-119. Every member reaches it: adding photographs is not an officer
 * act (§6), so there is nothing here to gate on `permissionLevel`.
 */
function PhotosRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <PhotosScreen
      unitId={unitId}
      unitName={membership.unitName}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/**
 * A parent's photo feed, across every unit their children are in.
 *
 * No membership check, for the reason {@link FamilyThreadRoute} gives: a guardian holds none, and
 * the sibling {@link PhotosRoute} bounces exactly the person this exists for. `EventAccessPolicy`
 * decides which units reach the feed, and a parent with no children on a roster gets an empty one.
 */
function FamilyPhotosRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <PhotosScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * The unit's lists, in one place (TROOP-120).
 *
 * Any member, not officers only. The screens it springboards to already admit members and gate
 * their own controls; narrowing here would take the roster away from members, which is an access
 * change wearing a navigation change's clothes.
 */
function ManageRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <ManageScreen
      unitId={unitId}
      unitName={membership.unitName}
      subgroupLabelSingular={membership.subgroupLabelSingular}
      subgroupLabelPlural={membership.subgroupLabelPlural}
      // Only what renders. The API is the enforcement point, and the channels route bounces a
      // member before its screen mounts.
      canManageChannels={membership.permissionLevel !== 'Member'}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/**
 * Reports on the unit's door — TROOP-12.
 *
 * Any member, for the reason {@link ManageRoute} gives: the screen serves a reviewer working a
 * queue and a reporter watching their own, and which one somebody is comes from the policy per
 * row rather than from this route. Narrowing here would take a Scout's own report away from them,
 * which is the whole thing §5 says makes flagging feel pointless.
 */
function ReportsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <ReportsScreen
      unitId={unitId}
      unitName={membership.unitName}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/**
 * The same screen through the family door, across every unit this parent has standing in.
 *
 * No membership check, for the reason {@link FamilyPhotosRoute} gives: a guardian holds none, and
 * the sibling route above bounces exactly the person this exists for.
 */
function FamilyReportsRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <ReportsScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * What reaches a person, and what does not (TROOP-192). Outside `/units/` because a person has one
 * set of these across every unit they are in -- and because a guardian holding no membership
 * anywhere must reach it, which a `/units/` route would 404.
 */
function NotificationSettingsRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <NotificationSettingsScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * What follows a person rather than a unit (TROOP-120): the calendar feed, their car, their own
 * lists, and the way out. Outside `/units/` because every one of those already is.
 */
function SettingsRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <SettingsScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * The leader's event roster (TROOP-130). Membership is checked here only so the route can bounce
 * somebody who is in no such unit; whether they may see the table is the server's answer, and the
 * screen renders the refusal it sends rather than guessing at it.
 */
function EventRosterRoute({ me }: { me: Me }) {
  const { unitId, eventId } = useParams();
  const navigate = useNavigate();

  if (!unitId || !eventId || !me.memberships.some((m) => m.unitId === unitId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <EventRosterScreen
      unitId={unitId}
      eventId={eventId}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/**
 * The troop's gear on loan (TROOP-167). Membership is checked here only so the route can bounce
 * somebody who is in no such unit; whether they may see what the unit has lent out is the server's
 * answer -- position only, so a youth Quartermaster passes and a plain member does not -- and the
 * screen renders the refusal it sends rather than restating the rule.
 */
function GearSheetsRoute({ me }: { me: Me }) {
  const { unitId, eventId } = useParams();
  const navigate = useNavigate();

  if (!unitId || !eventId || !me.memberships.some((m) => m.unitId === unitId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <GearSheetsScreen
      unitId={unitId}
      eventId={eventId}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function RosterRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <RosterScreen
      unitId={unitId}
      unitName={membership.unitName}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

/** Not under a unit: a car follows its owner, exactly as the calendar feed spans their units. */
function DrivingRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <DrivingScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/** Not under a unit: one feed covers every unit this person touches. */
function CalendarRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <CalendarSubscriptionScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * A parent's conversations. No membership check, and that is the point: a guardian holds none, and
 * the sibling unit route bounces exactly the person this exists for.
 */
function FamilyConversationsRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <FamilyConversationsScreen me={me} onNavigate={(path) => navigate(path)} />;
}

function FamilyRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <FamilyScreen me={me} onNavigate={(path) => navigate(path)} />;
}

/**
 * A parent reading their child's thread.
 *
 * No membership check, and that is the point: a guardian holds none, and the sibling
 * {@link ThreadRoute} bounces exactly the person this route exists for. There is nothing to check
 * here at all -- the API decides, with ConversationAccessPolicy, and a thread this parent may not
 * read is a 404 the screen renders as "could not load".
 */
function FamilyThreadRoute({ me }: { me: Me }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();

  if (!conversationId) {
    return <Navigate to="/family" replace />;
  }

  return (
    <FamilyThreadScreen
      conversationId={conversationId}
      me={me}
      meId={me.personId}
      onNavigate={(path) => navigate(path)}
      onBack={() => navigate('/family/conversations')}
      onOpenAnchor={(path) => navigate(path)}
    />
  );
}

/**
 * A parent reading one event.
 *
 * No membership check, for the reason {@link FamilyThreadRoute} gives: a guardian holds none, and
 * the sibling {@link EventRoute} bounces exactly the person this exists for. `EventAccessPolicy`
 * decides, and an event this parent has no standing in is a 404 the screen renders as "could not
 * load" — the same answer a genuinely missing event gets.
 */
function FamilyEventRoute({ me }: { me: Me }) {
  const { eventId } = useParams();
  const navigate = useNavigate();

  if (!eventId) {
    return <Navigate to="/family" replace />;
  }

  return (
    <FamilyEventScreen
      eventId={eventId}
      me={me}
      meId={me.personId}
      onNavigate={(path) => navigate(path)}
      onBack={() => navigate('/family')}
      onOpenThread={(conversationId) => navigate(`/family/conversations/${conversationId}`)}
    />
  );
}

function AdminRoute({ me }: { me: Me }) {
  const navigate = useNavigate();
  return <AdminInvitesScreen me={me} onNavigate={(path) => navigate(path)} />;
}

function NewConversationRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <NewConversationScreen
      unitId={unitId}
      subgroupLabelSingular={membership.subgroupLabelSingular}
      subgroupLabelPlural={membership.subgroupLabelPlural}
      // The API is the enforcement point; this only decides which options render,
      // so a member is not shown a choice that would be refused.
      canCreateForRoster={membership.permissionLevel !== 'Member'}
      onCreated={(conversationId) =>
        navigate(`/units/${unitId}/conversations/${conversationId}`, { replace: true })
      }
      onCancel={() => navigate(`/units/${unitId}`)}
    />
  );
}

/**
 * What the unit does about photographs — TROOP-225.
 *
 * Any member, not officers only, for the reason {@link ManageRoute} gives: the screen renders the
 * unit's answers as facts to everybody and as controls only to the people the API would accept a
 * write from, and whether somebody is one of those is `canChange` on the payload rather than
 * anything this route can work out — it reads a classification as well as a position, and
 * `/auth/me` carries no classification at all.
 */
function PhotoSettingsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <PhotoSettingsScreen
      unitId={unitId}
      unitName={membership.unitName}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function ChannelSettingsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  // A member is bounced rather than shown a page of controls the API would refuse. The API is
  // still the enforcement point; this only decides what renders.
  if (!unitId || !membership || membership.permissionLevel === 'Member') {
    return <Navigate to={unitId ? `/units/${unitId}/conversations` : '/'} replace />;
  }

  return (
    <ChannelSettingsScreen
      unitId={unitId}
      unitName={membership.unitName}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function ConversationsRoute({ me }: { me: Me }) {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const membership = me.memberships.find((m) => m.unitId === unitId);

  if (!unitId || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <ConversationsScreen
      unitId={unitId}
      unitName={membership.unitName}
      subgroupLabelPlural={membership.subgroupLabelPlural}
      // Only what renders. The API is the enforcement point -- a member starting a roster-scoped
      // thread is refused there -- so this decides which words the button uses, not who may act.
      canStartRosterThreads={membership.permissionLevel !== 'Member'}
      me={me}
      onNavigate={(path) => navigate(path)}
    />
  );
}

function ThreadRoute({ me }: { me: Me }) {
  const { unitId, conversationId } = useParams();
  const navigate = useNavigate();

  if (!unitId || !conversationId || !me.memberships.some((m) => m.unitId === unitId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <LiveThreadScreen
      unitId={unitId}
      conversationId={conversationId}
      me={me}
      meId={me.personId}
      onNavigate={(path) => navigate(path)}
      // Back to the conversations page, which is where a thread is now reached from. It used to be
      // the calendar, because that was the only way in.
      onBack={() => navigate(`/units/${unitId}/conversations`)}
      onOpenAnchor={(path) => navigate(path)}
    />
  );
}

/**
 * Signed-in landing. Not a dashboard: it forwards to the unit page, which is the calendar, and
 * for a parent with no membership to their family page instead.
 */
function SignedInHome({ me }: { me: Me }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (me.memberships.length > 0) {
      navigate(`/units/${me.memberships[0].unitId}`, { replace: true });
    } else if (me.isGuardian) {
      // A parent holds no membership by design, so without this they land on
      // "you're not on a roster yet" -- which is true and useless, and is the
      // screen the person who has to set up their child would have seen.
      navigate('/family', { replace: true });
    } else if (me.isSystemAdmin) {
      navigate('/admin/invites', { replace: true });
    }
  }, [me, navigate]);

  // Signed in, no unit, not a parent, not a platform admin.
  if (me.memberships.length === 0 && !me.isGuardian && !me.isSystemAdmin) {
    return (
      <main
        className="flex min-h-screen flex-col"
        style={{ background: 'var(--color-surface-page)', padding: 'var(--space-7) var(--gutter)' }}
      >
        <div style={{ maxWidth: '32rem' }}>
          <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
            You&rsquo;re signed in
          </h1>
          <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
            You&rsquo;re not on a unit roster yet. Ask a leader in your unit to add you, then come
            back.
          </p>
        </div>
      </main>
    );
  }

  // A redirect is already queued by the effect above; rendering anything here would appear for one
  // frame and then be replaced.
  return null;
}

/**
 * Signed in, but waiting on a guardian. Shown instead of every other route,
 * because the API refuses them anyway — and a login screen they have already
 * used would be a loop with no explanation.
 */
function WaitingOnGuardian({ me }: { me: Me }) {
  return (
    <main
      className="flex min-h-screen flex-col"
      style={{ background: 'var(--color-surface-page)', padding: 'var(--space-7) var(--gutter)' }}
    >
      <div style={{ maxWidth: '34rem' }}>
        <h1 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          Almost there, {me.displayName.split(' ')[0]}
        </h1>
        <p
          className="t-body"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 var(--space-5)' }}
        >
          {me.accessBlockedReason}
        </p>
        <button
          type="button"
          className="link-button t-body"
          onClick={() => {
            void authApi.signOut().finally(() => {
              window.location.href = '/';
            });
          }}
        >
          Sign out
        </button>
      </div>
    </main>
  );
}

function Shell() {
  const { me, refresh } = useSession();

  // For the sign-in route's `next`, which the code door hands back on the verify.
  const navigate = useNavigate();

  // Nothing, deliberately, while the session resolves. The document already carries the page
  // background, so this is a steady empty page rather than a flash of placeholder text that is
  // replaced a few milliseconds later -- the session call is fast, and something appearing only to
  // be swapped out is more disruptive than a brief moment of calm.
  if (me === undefined) {
    return null;
  }

  if (me?.accessBlockedReason) {
    return <WaitingOnGuardian me={me} />;
  }

  return (
    <Routes>
      <Route path="/start" element={<StartRoute onSignedIn={refresh} />} />
      <Route path="/claim" element={<ClaimRoute onSignedIn={refresh} />} />

      <Route
        path="/sign-in"
        element={
          me ? (
            <Navigate to="/" replace />
          ) : (
            <SignInScreen
              onSignedIn={async ({ redirectPath }) => {
                await refresh();

                // Honoured here rather than by the API, which is what changed with the code door
                // (TROOP-222): redemption used to be a GET that redirected, so the path was
                // applied by the server. It now comes back on the verify. Sanitised into a
                // site-relative path when the code was issued, so it cannot point at another
                // origin; "/" needs no navigate, since falling through renders the home route.
                if (redirectPath && redirectPath !== '/') {
                  navigate(redirectPath, { replace: true });
                }
              }}
            />
          )
        }
      />

      {/* Public: every page footer links at these three, so they answer for a signed-in member as
          readily as for the committee chair evaluating the product, and none of them takes `me`.
          The one reader they do not reach is a youth waiting on a guardian — `Shell`
          short-circuits to `WaitingOnGuardian` above this, before any route matches, which is rule
          11 working rather than something to route around. TROOP-217, TROOP-203.

          Mapped from the same list `Footer` renders, through a `Record` keyed on the path: a
          destination added to `FOOTER_LINKS` with no screen behind it fails to compile. That is
          the point of the list, since what these three routes are replacing is a year of anchors
          pointing at nothing that no test, build or browser could notice. */}
      {FOOTER_LINKS.map((link) => (
        <Route key={link.path} path={link.path} element={FOOTER_SCREENS[link.path]} />
      ))}

      {/* Public marketing page. The demo routes that ran on fixtures are gone: the event screen
          they existed to show is now the real one, behind a unit. It takes no props at all now —
          the `onOpenEvent` this passed was `() => undefined`, which is how a dead primary button
          survived a clean-up (TROOP-220). */}
      <Route path="/" element={me ? <SignedInHome me={me} /> : <HomeScreen />} />

      <Route
        path="/units/:unitId"
        element={me ? <UnitHomeRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/events/new"
        element={me ? <NewEventRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/announcements/new"
        element={me ? <NewAnnouncementRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/events/:eventId"
        element={me ? <EventRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/events/:eventId/edit"
        element={me ? <EditEventRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/manage"
        element={me ? <ManageRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/photo-settings"
        element={me ? <PhotoSettingsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/settings"
        element={me ? <SettingsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/settings/notifications"
        element={me ? <NotificationSettingsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/reports"
        element={me ? <ReportsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family/reports"
        element={me ? <FamilyReportsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/photos"
        element={me ? <PhotosRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/roster"
        element={me ? <RosterRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/events/:eventId/roster"
        element={me ? <EventRosterRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/events/:eventId/gear"
        element={me ? <GearSheetsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/subgroups"
        element={me ? <SubgroupsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/event-types"
        element={me ? <EventTypesRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/packing-lists"
        element={me ? <PackingListsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family"
        element={me ? <FamilyRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family/photos"
        element={me ? <FamilyPhotosRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family/conversations"
        element={me ? <FamilyConversationsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family/conversations/:conversationId"
        element={me ? <FamilyThreadRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/family/events/:eventId"
        element={me ? <FamilyEventRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/calendar"
        element={me ? <CalendarRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      {/* Not under a unit: a car and a licence follow their owner between units. */}
      <Route
        path="/driving"
        element={me ? <DrivingRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/conversations"
        element={me ? <ConversationsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/channels"
        element={me ? <ChannelSettingsRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/conversations/new"
        element={me ? <NewConversationRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/units/:unitId/conversations/:conversationId"
        element={me ? <ThreadRoute me={me} /> : <Navigate to="/sign-in" replace />}
      />

      <Route
        path="/admin/invites"
        element={
          me?.isSystemAdmin ? <AdminRoute me={me} /> : <Navigate to="/" replace />
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* One connection for the whole app. Every screen that wants live updates reads the same
          stream, and nothing subscribes per conversation -- see useLiveMessages for why. */}
      <LiveMessagesProvider>
        <Shell />
      </LiveMessagesProvider>
    </BrowserRouter>
  );
}
